import { useApolloClient } from "@apollo/client";
import { Modal, ModalOpenButton } from "@msys/ui";
import { Box, Button, DialogContentText, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import { RequestSupplierModal } from "../crm-companies/modals/RequestSupplierModal.js";
import {
  useCreateNewSupplierMutation,
  useSubscribeToSupplierMutation,
} from "./SupplierCreateModal.generated.js";
import { SupplierSelect } from "./SupplierSelect.js";

interface AddSupplierFormValues {
  pimSupplier: { id: string; title: string } | null;
}

interface Props {
  handleClose: () => void;
  handleComplete: (pimSupplierId: string, handleClose: () => void) => void;
  refetchQueries?: string[];
}

export const SupplierCreateModal = ({
  handleClose,
  handleComplete,
  refetchQueries,
}: Props) => {
  const { t } = useTranslate([
    "SupplierNew",
    "Global",
    "Product",
    "OrganisationInvite",
  ]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();

  const [subscribeToSupplier] = useSubscribeToSupplierMutation({
    client,
    refetchQueries,
  });

  const [copySupplierOrganisationAsCrm] = useCreateNewSupplierMutation({
    client,
    refetchQueries,
  });

  const handleCreateSupplier = React.useCallback(
    async (pimSupplierId: string) => {
      await subscribeToSupplier({
        variables: {
          input: {
            supplierId: pimSupplierId,
          },
        },
      });

      const result = await copySupplierOrganisationAsCrm({
        variables: {
          input: {
            pimSupplierId: pimSupplierId,
          },
        },
      });

      const crmSupplierOrg =
        result.data?.copySupplierOrganisationAsCrm.crmOrganisation;
      if (!crmSupplierOrg)
        throw new Error("Failed to copy supplier organisation as CRM");

      handleComplete(pimSupplierId, handleClose);
      enqueueSnackbar(
        t("Supplier added to organisation", {
          ns: "SupplierNew",
        })
      );
    },
    [
      subscribeToSupplier,
      copySupplierOrganisationAsCrm,
      enqueueSnackbar,
      handleClose,
      handleComplete,
      t,
    ]
  );

  const onSubmit = React.useCallback(
    async (values: AddSupplierFormValues) => {
      if (!values.pimSupplier) throw new Error("Supplier not selected");

      await handleCreateSupplier(values.pimSupplier.id);
    },
    [handleCreateSupplier]
  );

  const initialValues: AddSupplierFormValues = {
    pimSupplier: null,
  };
  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        pimSupplier: Yup.object()
          .shape({ id: Yup.string().required() })
          .label(t("Supplier", { ns: "Product" }))
          .nullable()
          .required(),
      }),
    [t]
  );

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<AddSupplierFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({ values, setFieldValue, isSubmitting, isValid }) => (
        <Modal
          title={t("Add supplier")}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: {
                variant: "text",
                disabled: isSubmitting,
              },
            },
            {
              label: t("Create", { ns: "Global" }),
              buttonProps: {
                type: "submit",
                form: formId,
                loading: isSubmitting,
                disabled: !isValid,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack spacing={1}>
              <SupplierSelect
                label={t("Select an existing supplier", { ns: "SupplierNew" })}
                isSubscribed={false}
                required={true}
                disabled={isSubmitting}
                productSupplierId={values.pimSupplier?.id ?? null}
                onChange={productSupplier => {
                  setFieldValue("pimSupplier", productSupplier);
                }}
              />
              <Box
                sx={{
                  paddingTop: 1,
                }}
              >
                <DialogContentText
                  sx={{
                    textAlign: "center",
                    marginBottom: 0,
                  }}
                >
                  <Box>
                    {t("Not finding your supplier?", {
                      ns: "OrganisationInvite",
                    })}
                  </Box>
                  <ModalOpenButton
                    Modal={RequestSupplierModal}
                    modalProps={{
                      title: t("Request creation of supplier", {
                        ns: "SupplierNew",
                      }),
                    }}
                  >
                    <Button color="secondary" size="small">
                      {t("Request a supplier", {
                        ns: "OrganisationInvite",
                      })}
                    </Button>
                  </ModalOpenButton>
                </DialogContentText>
              </Box>
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
